import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { CustomRxApiService } from '@shared/services/custom-rx-api.service';
import { MaterialStore } from '@modules/material/state/material-store';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { EntitiesByLabIdDto } from '@shared/models/entities-by-lab-id-dto';
import { ProcedureMap } from '@shared/models/procedure-map';
import { SendToOption } from '@shared/models/send-to-option';
import { isFixedRestorativeRestoLab } from '@shared/utils/send-to-utils';
import type { Maybe } from '@/types/globals';

@Injectable({ providedIn: 'root' })
export class CustomMaterialsService {
	private readonly serviceName = 'CustomMaterialsService';

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly customRxApiService: CustomRxApiService,
		private readonly materialStore: MaterialStore,
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly logger: LoggerService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly hostPlatformService: HostPlatformService
	) {}

	/**
	 * Observes the current procedure map and selected lab, then triggers enrichment or reset of material rules based on their values.
	 * @returns Observable emitting the current [ProcedureMap, SendToOption]
	 */
	enrichMaterialRules(): Observable<[Maybe<ProcedureMap>, Maybe<SendToOption>]> {
		return combineLatest([this.orderQuery.procedureMap$, this.orderQuery.sendTo$]).pipe(
			tap(([procedureMap, sendTo]) => this.updateMaterialRules(procedureMap, sendTo))
		);
	}

	/**
	 * Loads custom materials if enabled and updates material rules.
	 * @returns Observable emitting the loaded custom material entities.
	 */
	loadCustomMaterials(): Observable<EntitiesByLabIdDto[]> {
		return combineLatest([
			this.shellQuery.companies$,
			this.shellQuery.isReadOnly$,
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.MaterialSpecificationCustomizedRx)
		]).pipe(
			filter(([, isReadOnly, useCustomMaterials]) => !isReadOnly && useCustomMaterials && !this.hostPlatformService.isOfflineMode()),
			take(1),
			switchMap(([linkedCompanies]) => this.loadCustomRxEntities(linkedCompanies.map(company => company.Id))),
			tap(customEntities => {
				if (customEntities.length > 0) {
					this.materialStore.updateFromApiEntities(customEntities);
					this.updateMaterialRules(this.orderQuery.procedureMap, this.orderQuery.sendTo);
				}
			})
		);
	}

	/**
	 * Checks if a lab has custom materials or specifications.
	 * @param labId - The lab ID to check
	 * @returns true if the lab has custom materials or specifications, false otherwise
	 */
	hasCustomEntitiesForLab(labId: Maybe<number>): boolean {
		if (!labId) {
			return false;
		}

		const state = this.materialStore.getValue();

		const hasNonBridgeEntities = !!state.nonBridgeDictionary[labId] && Object.keys(state.nonBridgeDictionary[labId]).length > 0;
		const hasBridgeEntities = !!state.bridgeDictionary[labId] && Object.keys(state.bridgeDictionary[labId]).length > 0;

		return hasNonBridgeEntities || hasBridgeEntities;
	}

	private loadCustomRxEntities(labIds: number[]): Observable<EntitiesByLabIdDto[]> {
		if (labIds.length > 0) {
			const uniqueIds = [...new Set(labIds)];

			return this.customRxApiService.getAllEntitiesByLabIds(uniqueIds).pipe(
				catchError(err => {
					this.logger?.warn?.('getAllEntitiesByLabIds failed', { module: this.serviceName, error: err });

					return of([]);
				})
			);
		}

		return of([]);
	}

	private updateMaterialRules(procedureMap: Maybe<ProcedureMap>, sendTo: Maybe<SendToOption>) {
		if (procedureMap && sendTo && isFixedRestorativeRestoLab(procedureMap, sendTo)) {
			this.rxMaterialRulesService.enrichMaterialRules(sendTo.Id as number);
		} else {
			this.rxMaterialRulesService.resetMaterialRules();
		}
	}
}
